import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckIcon from "@material-ui/icons/Check";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_SimpleSelect from "../../../Atoms/Inputs/Selects/A_SimpleSelect";
import A_DatePicker from "../../../Atoms/Inputs/DateTimePickers/A_DatePicker";
import A_Switch from "../../../Atoms/Inputs/Switches/A_Switch";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import { validateFirmDraft } from "../../../../../domain/professionalContacts/professionalContacts.rules";
import {
  FIRM_TYPES,
  DISTRESSED_FIRM_TYPES,
  firmTypeLabel,
} from "../../../../../domain/professionalContacts/professionalContacts.types";
import { api, toBankAccountUpsert } from "./ProfessionalContacts.helper";
import ContactRowsEditor from "./ContactRowsEditor";
import AddressRowsEditor from "./AddressRowsEditor";
import BankAccountRowsEditor from "./BankAccountRowsEditor";
import useStyles from "./ProfessionalContacts.styles";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";

const blankContact = (main) => ({
  _key: `new-${Date.now()}-c`,
  name: "",
  role: "",
  email: "",
  phone: "",
  isMain: main,
});
const blankAddress = (preferred) => ({
  _key: `new-${Date.now()}-a`,
  building: "",
  street: "",
  town: "",
  postcode: "",
  country: "United Kingdom",
  isPreferred: preferred,
});
const blankBankAccount = (main) => ({
  _key: `new-${Date.now()}-b`,
  beneficiaryName: "",
  bankType: "",
  accountNumber: "",
  sortCode: "",
  bankName: "",
  town: "",
  postcode: "",
  country: "",
  address: "",
  isMain: main,
});

const withRowKeys = (rows, keyPrefix) =>
  (rows || []).map((row, index) => ({
    ...row,
    _key: row.id || `${keyPrefix}-${index}`,
  }));

const epochSecondsToIso = (value) =>
  value ? new Date(value * 1000).toISOString().slice(0, 10) : null;

// eslint-disable-next-line no-unused-vars
const stripKeys = (rows) => rows.map(({ _key, ...rest }) => rest);

const FirmForm = ({
  firmId,
  defaultType,
  initialAppend,
  onCancel,
  onSaved,
}) => {
  const classes = useStyles();
  const isEdit = Boolean(firmId);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const bodyRef = useRef(null);

  const revealValidationSummary = () => {
    const node = bodyRef.current;
    if (node && typeof node.scrollTo === "function")
      node.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    let applied = false;
    const applyAppends = (base) => {
      if (applied) return base;
      applied = true;
      const next = { ...base };
      if (initialAppend?.appendContact)
        next.contacts = [
          ...next.contacts,
          blankContact(next.contacts.length === 0),
        ];
      if (initialAppend?.appendAddress)
        next.addresses = [
          ...next.addresses,
          blankAddress(next.addresses.length === 0),
        ];
      if (initialAppend?.appendBankAccount)
        next.bankAccounts = [
          ...next.bankAccounts,
          blankBankAccount(next.bankAccounts.length === 0),
        ];
      return next;
    };

    if (isEdit) {
      setLoading(true);
      api
        .getFirm(firmId)
        .then((firm) => {
          setDraft(
            applyAppends({
              ...firm,
              contacts: withRowKeys(firm.contacts, "c"),
              addresses: withRowKeys(firm.addresses, "a"),
              bankAccounts: withRowKeys(firm.bankAccounts, "b"),
            })
          );
        })
        .catch((err) => setLoadError(err))
        .finally(() => setLoading(false));
    } else {
      setDraft(
        applyAppends({
          type:
            defaultType && FIRM_TYPES.includes(defaultType)
              ? defaultType
              : FIRM_TYPES[0],
          name: "",
          mainPhone: "",
          email: "",
          piiValue: undefined,
          piiExpiry: null,
          panelApproved: false,
          panelApprovedDate: null,
          distressedOnly: false,
          active: true,
          noFurtherInstructions: false,
          notes: "",
          contacts: [blankContact(true)],
          addresses: [blankAddress(true)],
          bankAccounts: [],
        })
      );
    }
  }, [firmId]);

  if (loading) return <LoaderSkeleton />;
  // On the very first paint of an Add, the draft is still null because the effect that seeds it
  // has not run yet. Only a real load failure gets the error state — otherwise that first paint
  // flashed "Couldn't load this company" before the empty form appeared.
  if (!draft) {
    if (!loadError) return <LoaderSkeleton />;
    return (
      <div className={classes.validationSummary}>
        <ErrorOutlineIcon className={classes.validationIcon} />
        <span>
          {loadError.message || "Couldn't load this company. Please try again."}
        </span>
      </div>
    );
  }

  const setField = (field, value) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    const { valid, missing: problems } = validateFirmDraft(draft);
    if (!valid) {
      setMissing(problems);
      revealValidationSummary();
      return;
    }
    setMissing([]);
    setSaving(true);
    const payload = {
      type: draft.type,
      name: draft.name,
      mainPhone: draft.mainPhone,
      email: draft.email,
      piiValue: draft.piiValue ? Number(draft.piiValue) : undefined,
      piiExpiry: draft.piiExpiry || undefined,
      panelApproved: draft.panelApproved,
      panelApprovedDate: draft.panelApprovedDate || undefined,
      distressedOnly: draft.distressedOnly,
      active: draft.active,
      noFurtherInstructions: draft.noFurtherInstructions,
      notes: draft.notes,
      contacts: stripKeys(draft.contacts),
      addresses: stripKeys(draft.addresses),
      bankAccounts: draft.bankAccounts.map(toBankAccountUpsert),
    };
    try {
      const result = isEdit
        ? await api.updateFirm(firmId, payload)
        : await api.createFirm(payload);
      onSaved(result);
    } catch (err) {
      setMissing(
        err.fieldErrors?.length
          ? err.fieldErrors.map((fe) => `${fe.field}: ${fe.message}`)
          : [err.message || "Save failed"]
      );
      revealValidationSummary();
    } finally {
      setSaving(false);
    }
  };

  const showPII = draft.type !== "BorrowerSolicitor";
  const showDistressedToggle = !DISTRESSED_FIRM_TYPES.includes(draft.type);

  return (
    <div className={classes.formShell}>
      <div className={classes.formTopBar}>
        <button
          type="button"
          aria-label="Back"
          title="Back"
          className={classes.formBackButton}
          onClick={onCancel}
          disabled={saving}
        >
          <ArrowBackIcon fontSize="small" />
        </button>
        <Typography component="h3" className={classes.formTitle}>
          {isEdit ? "Edit Company" : "Add Company"}
        </Typography>
      </div>

      <div className={classes.formBody} ref={bodyRef}>
        {missing.length > 0 && (
          <div className={classes.validationSummary}>
            <ErrorOutlineIcon className={classes.validationIcon} />
            <span>Please complete the following: {missing.join("; ")}.</span>
          </div>
        )}

        <div
          className={clsx(classes.sectionHeading, classes.sectionHeadingFirst)}
        >
          Company details
        </div>
        <div className={classes.sectionCard}>
          <div className={classes.formGrid}>
            <A_SimpleSelect
              id="firm-type"
              label="Type"
              className={classes.field}
              size="small"
              style={{ margin: 0, width: "100%" }}
              defaultValue={draft.type}
              options={FIRM_TYPES.map((type) => ({
                value: type,
                label: firmTypeLabel(type),
              }))}
              optionLabel="label"
              optionValue="value"
              onChange={(value) => setField("type", value)}
            />
            <A_TextField
              id="firm-name"
              label="Company Name *"
              className={classes.field}
              defaultValue={draft.name}
              onChange={(e) => setField("name", e.target.value)}
              fullWidth
              size="small"
              margin="none"
              variant="outlined"
            />
            <A_TextField
              id="firm-mainPhone"
              label="Main Contact Number *"
              className={classes.field}
              defaultValue={draft.mainPhone}
              onChange={(e) => setField("mainPhone", e.target.value)}
              fullWidth
              size="small"
              margin="none"
              variant="outlined"
            />
            <A_TextField
              id="firm-email"
              label="Company Email Address *"
              className={classes.field}
              defaultValue={draft.email}
              onChange={(e) => setField("email", e.target.value)}
              fullWidth
              size="small"
              margin="none"
              variant="outlined"
            />
          </div>
        </div>

        <ContactRowsEditor
          contacts={draft.contacts}
          onChange={(contacts) => setDraft((prev) => ({ ...prev, contacts }))}
        />
        <AddressRowsEditor
          addresses={draft.addresses}
          onChange={(addresses) => setDraft((prev) => ({ ...prev, addresses }))}
        />
        <BankAccountRowsEditor
          bankAccounts={draft.bankAccounts}
          onChange={(bankAccounts) =>
            setDraft((prev) => ({ ...prev, bankAccounts }))
          }
        />

        {showPII && (
          <>
            <div className={classes.sectionHeading}>
              Professional indemnity insurance &amp; panel
            </div>
            <div className={classes.sectionCard}>
              <div className={classes.formGrid}>
                <A_TextField
                  id="firm-piiValue"
                  label="PII Value (£)"
                  className={classes.field}
                  type="number"
                  inputType="value"
                  defaultValue={draft.piiValue}
                  onChange={(e) => setField("piiValue", e.target.value)}
                  fullWidth
                  size="small"
                  margin="none"
                  variant="outlined"
                />
                <A_DatePicker
                  id="firm-piiExpiry"
                  label="PII Expiry"
                  className={classes.field}
                  fullWidth
                  inputVariant="outlined"
                  margin="none"
                  size="small"
                  defaultValue={draft.piiExpiry}
                  onChange={(epochSeconds) =>
                    setField("piiExpiry", epochSecondsToIso(epochSeconds))
                  }
                />
                <A_DatePicker
                  id="firm-panelApprovedDate"
                  label="Panel Approved Date"
                  className={classes.field}
                  fullWidth
                  inputVariant="outlined"
                  margin="none"
                  size="small"
                  defaultValue={draft.panelApprovedDate}
                  onChange={(epochSeconds) =>
                    setField(
                      "panelApprovedDate",
                      epochSecondsToIso(epochSeconds)
                    )
                  }
                />
              </div>
            </div>
          </>
        )}

        <div className={classes.sectionHeading}>Notes &amp; status</div>
        <div className={classes.sectionCard}>
          <A_TextField
            id="firm-notes"
            label="Notes"
            className={classes.field}
            defaultValue={draft.notes}
            onChange={(e) => setField("notes", e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
            margin="none"
            variant="outlined"
          />

          <div className={classes.formFooter}>
            {showPII && (
              <span className={classes.toggleWrap}>
                <A_Switch
                  label="Panel Approved"
                  labelPlacement="end"
                  size="small"
                  checked={Boolean(draft.panelApproved)}
                  onChange={(e) => setField("panelApproved", e.target.checked)}
                />
              </span>
            )}
            {showDistressedToggle && (
              <span className={classes.toggleWrap}>
                <A_Switch
                  label="Distressed Scenarios Only"
                  labelPlacement="end"
                  size="small"
                  checked={Boolean(draft.distressedOnly)}
                  onChange={(e) => setField("distressedOnly", e.target.checked)}
                />
              </span>
            )}
            <span className={classes.toggleWrap}>
              <A_Switch
                label="Active"
                labelPlacement="end"
                size="small"
                checked={Boolean(draft.active)}
                onChange={(e) => setField("active", e.target.checked)}
              />
            </span>
            <span className={classes.toggleWrap}>
              <A_Switch
                label="No Further Instructions"
                labelPlacement="end"
                size="small"
                checked={Boolean(draft.noFurtherInstructions)}
                onChange={(e) =>
                  setField("noFurtherInstructions", e.target.checked)
                }
              />
            </span>
          </div>
        </div>
      </div>

      <div className={classes.formActionBar}>
        <A_Button
          label="Cancel"
          size="small"
          premiumVariant="secondary"
          onClick={onCancel}
          disabled={saving}
        />
        <A_SecuredAction permission={pageSecurityActions.UPDATE}>
          <A_Button
            label="Save Company"
            size="small"
            premiumVariant="primary"
            startIcon={<CheckIcon fontSize="small" />}
            onClick={handleSave}
            disabled={saving}
          />
        </A_SecuredAction>
      </div>
    </div>
  );
};

FirmForm.propTypes = {
  firmId: PropTypes.string,
  defaultType: PropTypes.string,
  initialAppend: PropTypes.shape({
    appendContact: PropTypes.bool,
    appendAddress: PropTypes.bool,
    appendBankAccount: PropTypes.bool,
  }),
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

FirmForm.defaultProps = {
  initialAppend: {},
};

export default FirmForm;
